/**
 * Shared plumbing for the domain mocks — envelope, localization, pagination.
 * Error messages mirror the x-error-codes registries of geo.yaml and
 * merchants.yaml verbatim (the real backend localizes identically).
 */

import type { PageMeta } from "../contracts/common";
import type { TransportRequest, TransportResponse } from "../transport";

export type MockLocale = "ar" | "en";

const MESSAGES: Record<string, { readonly ar: string; readonly en: string }> = {
  VALIDATION_FAILED: { ar: "البيانات المدخلة غير صالحة", en: "The submitted data is invalid" },
  AUTH_TOKEN_INVALID: { ar: "جلسة غير صالحة، الرجاء تسجيل الدخول مجدداً", en: "Invalid session, please sign in again" },
  PERMISSION_DENIED: { ar: "ليست لديك صلاحية لهذا الإجراء", en: "You do not have permission for this action" },
  RESOURCE_NOT_FOUND: { ar: "العنصر غير موجود", en: "Resource not found" },
  IDEMPOTENCY_KEY_MISSING: { ar: "طلب غير صالح (مفتاح التكرار مفقود)", en: "Invalid request (missing idempotency key)" },
  ZONE_NAME_TAKEN: { ar: "اسم المنطقة مستخدم مسبقاً", en: "Zone name already in use" },
  ZONE_ACTIVE: { ar: "يجب تعطيل المنطقة قبل حذفها", en: "Disable the zone before deleting it" },
  ZONE_DISABLED: { ar: "هذه المنطقة غير مفعّلة حالياً", en: "This zone is currently disabled" },
  OWNER_ALREADY_HAS_MERCHANT: { ar: "هذا المستخدم يملك متجراً مسجلاً بالفعل", en: "This user already owns a registered merchant" },
  // merchants.yaml v1.4.0 x-error-codes (M7-42 — verbatim registry copy)
  PHONE_TAKEN: { ar: "رقم الهاتف مسجل بحساب موجود مسبقاً — اربط المستخدم الحالي بدلاً من إنشاء حساب جديد", en: "This phone number already belongs to an existing account — link that user instead of creating a new one" },
  APPLICATION_ALREADY_DECIDED: { ar: "تم البت في هذا الطلب مسبقاً", en: "This application has already been decided" },
  MULTI_BRANCH_DISABLED: { ar: "المنصة تعمل بنظام الفرع الواحد — لا يمكن إضافة فرع ثانٍ", en: "The platform is single-branch — a second branch cannot be added" },
  BRANCH_ZONE_INVALID: { ar: "المنطقة المحددة غير متاحة", en: "The selected zone is not available" },
  STAFF_USER_NOT_FOUND: { ar: "لا يوجد مستخدم مسجل بهذا الرقم", en: "No registered user with this phone number" },
  // merchants.yaml v1.21.0 x-error-codes (M7-326 — owner password reset)
  NO_OWNER_ACCOUNT: { ar: "لا يوجد حساب مالك", en: "No owner account" },
  // identity.yaml x-error-codes — RBAC admin (M7-23)
  ROLE_KEY_TAKEN: { ar: "معرّف الدور مستخدم مسبقاً", en: "Role key already in use" },
  ROLE_SYSTEM_IMMUTABLE: { ar: "لا يمكن تعديل أدوار النظام", en: "System roles cannot be modified" },
  ROLE_IN_USE: { ar: "الدور مُسند لمستخدمين حالياً", en: "Role is currently assigned to users" },
  // drivers.yaml x-error-codes — deep driver management (M7-23)
  DRIVER_USER_NOT_FOUND: { ar: "لا يوجد مستخدم مسجل بهذا الرقم", en: "No Identity user with that phone" },
  DRIVER_ALREADY_EXISTS: { ar: "هذا المستخدم لديه ملف سائق بالفعل", en: "This user already has a driver profile" },
  DRIVER_BLOCK_REASON_RESERVED: {
    ar: "سبب الحظر هذا محجوز للنظام",
    en: "That block reason is reserved for the wallet system",
  },
  DOCUMENT_ALREADY_REVIEWED: { ar: "تمت مراجعة هذه الوثيقة مسبقاً", en: "This document has already been reviewed" },
  STAFF_ALREADY_EMPLOYED: { ar: "هذا المستخدم مضاف مسبقاً لهذا المتجر", en: "This user is already staff of this merchant" },
  THEME_VERSION_CONFLICT: { ar: "تم تعديل السمة من جهة أخرى — أعد التحميل ثم كرر التعديل", en: "The theme was changed elsewhere — reload and reapply your edits" },
  // dispatch.yaml x-error-codes (M6-05, M6-19)
  DRIVER_BUSY: { ar: "لديك طلب نشط بالفعل", en: "You already have an active order" },
  DRIVER_NOT_ELIGIBLE: { ar: "السائق غير متاح للإسناد", en: "The driver is not eligible for assignment" },
  OVERRIDE_ALREADY_DECIDED: { ar: "تم البت في طلب الاستثناء مسبقاً", en: "This exception request has already been decided" },
  // dispatch.yaml v1.3.0 x-error-codes (M7-81)
  DISPATCH_IN_PROGRESS: {
    ar: "يتم عرض هذا الطلب على السائقين حالياً",
    en: "This order is already being offered to drivers",
  },
  // notifications.yaml x-error-codes (M6-19)
  TEMPLATE_PLACEHOLDER_UNKNOWN: { ar: "النص يحتوي على رمز غير معروف", en: "Body references an unknown placeholder token" },
  TEMPLATE_NOT_TESTABLE: { ar: "لا يمكن إرسال هذا القالب تجريبياً", en: "This template cannot be test-sent" },
  // orders.yaml x-error-codes (M6-04)
  INVALID_TRANSITION: { ar: "لا يمكن تغيير حالة الطلب من وضعها الحالي", en: "The order cannot move to that status from its current state" },
  CANCELLATION_NOT_ALLOWED: { ar: "لا يمكن إلغاء الطلب في هذه المرحلة", en: "The order cannot be cancelled at this stage" },
  ORDER_NOT_EDITABLE: { ar: "لا يمكن تعديل الطلب بعد الاستلام", en: "The order cannot be edited after pickup" },
  // wallets.yaml + subscriptions.yaml x-error-codes (M6-06)
  WALLET_CREDIT_LIMIT_EXCEEDED: { ar: "تجاوز حد الائتمان للمحفظة", en: "Wallet credit limit exceeded" },
  WALLET_FROZEN: { ar: "المحفظة موقوفة", en: "Wallet is frozen" },
  DRIVER_PLAN_INVALID: { ar: "بيانات خطة السائق غير صالحة", en: "Invalid driver plan configuration" },
  MERCHANT_ALREADY_SUBSCRIBED: { ar: "التاجر مشترك بالفعل باشتراك فعّال", en: "Merchant already has an active subscription" },
  INVOICE_NOT_PAYABLE: { ar: "لا يمكن دفع هذه الفاتورة", en: "This invoice is not payable" },
  // promotions.yaml x-error-codes (M6-07)
  PROMOTION_NOT_EDITABLE: { ar: "لا يمكن تعديل عرض بعد تفعيله", en: "Promotion economics are frozen after activation" },
  PROMOTION_NOT_DELETABLE: { ar: "لا يمكن حذف عرض له سجل استخدام", en: "Cannot delete a promotion past DRAFT or with applications" },
  PROMOTION_STATUS_INVALID: { ar: "انتقال حالة غير مسموح", en: "Status transition not in the whitelist" },
  PROMOTION_KIND_MISMATCH: { ar: "أكواد القسائم متاحة لعروض الكوبون فقط", en: "Coupon codes exist only on COUPON promotions" },
  FUNDING_SPLIT_INVALID: { ar: "نسب تمويل الخصم غير صحيحة", en: "Funding split percentages are invalid for the funder" },
  COUPON_CODE_DUPLICATE: { ar: "رمز القسيمة مستخدم بالفعل", en: "Coupon code already exists" },
  // settings.yaml + payments.yaml x-error-codes (M6-07)
  SETTING_VALUE_INVALID: { ar: "قيمة الإعداد غير صالحة", en: "Invalid setting value" },
  FLAG_NOT_MERCHANT_SCOPED: { ar: "هذه الميزة لا تقبل تخصيصاً لكل تاجر", en: "This flag does not support per-merchant overrides" },
  // settings.yaml v1.1.0 (M7-36d phase gating)
  FLAG_LOCKED: { ar: "هذه الميزة مثبتة على الإيقاف من ملف النشر ولا يمكن تغييرها من اللوحة", en: "This flag is force-pinned off by the deploy override file and cannot be toggled from the panel" },
  PROVIDER_KEY_TAKEN: { ar: "معرّف المزود مستخدم مسبقاً", en: "Provider key already in use" },
  // marketing.yaml v1.0.0 x-error-codes (M7-106 — verbatim registry copy)
  BANNER_KIND_NOT_AVAILABLE: { ar: "إعلانات المنتجات متاحة في المرحلة الثانية", en: "Product banners are not available yet (phase 2)" },
  BANNER_TARGET_NOT_FOUND: { ar: "المتجر المستهدف غير موجود", en: "The target store does not exist" },
  // merchants.yaml v1.11.0 x-error-codes (M7-147 — verbatim registry copy)
  CATEGORY_IN_USE: {
    ar: "لا يمكن حذف الفئة لأن هناك متاجر مرتبطة بها — أخفها بدلاً من ذلك",
    en: "This category cannot be deleted while stores are assigned to it — hide it instead",
  },
  CATEGORY_ORDER_INCOMPLETE: {
    ar: "يجب أن يتضمن الترتيب الجديد كل الفئات الحالية مرة واحدة بالضبط",
    en: "The new order must list every current category exactly once",
  },
  MENU_ORDER_INCOMPLETE: { ar: "قائمة الترتيب يجب أن تضم جميع صور القائمة مرة واحدة", en: "The order list must contain every menu image exactly once" },
  BANNER_ORDER_INCOMPLETE: { ar: "قائمة الترتيب يجب أن تضم جميع الإعلانات مرة واحدة", en: "The order list must contain every banner exactly once" },
  // marketing.yaml v1.4.0 x-error-codes (M7-253 — verbatim registry copy)
  COLLECTION_ORDER_INCOMPLETE: { ar: "قائمة الترتيب يجب أن تضم جميع المجموعات مرة واحدة", en: "The order list must contain every collection exactly once" },
  // marketing.yaml v1.5.0 x-error-codes (M7-265 — verbatim registry copy)
  SUBCATEGORY_ORDER_INCOMPLETE: { ar: "قائمة الترتيب يجب أن تضم جميع الأقسام الفرعية مرة واحدة", en: "The order list must contain every subcategory exactly once" },
  SLOT_ORDER_INCOMPLETE: { ar: "قائمة الترتيب يجب أن تضم جميع الخانات مرة واحدة", en: "The order list must contain every slot exactly once" },
  SLOT_KIND_MISMATCH: { ar: "هذا الإجراء متاح لخانات الإعلان فقط", en: "This action applies to banner slots only" },
  // marketing.yaml v1.6.0 x-error-codes (M7-268 — verbatim registry copy)
  HOME_ORDER_INCOMPLETE: { ar: "قائمة الترتيب يجب أن تضم جميع عناصر الرئيسية مرة واحدة", en: "The order list must contain every home item exactly once" },
  HOME_BANNER_ORDER_INCOMPLETE: { ar: "قائمة الترتيب يجب أن تضم جميع إعلانات المجموعة مرة واحدة", en: "The order list must contain every banner of the group exactly once" },
  HOME_ENTRY_KIND_MISMATCH: { ar: "هذا الإجراء لا ينطبق على نوع هذا العنصر", en: "This action does not apply to this entry kind" },
  // marketing.yaml v1.7.0 x-error-codes (M7-300 — verbatim copy of the Marketing module's lang/errors)
  POPUP_ORDER_INCOMPLETE: { ar: "قائمة الترتيب يجب أن تضم جميع النوافذ الإعلانية مرة واحدة", en: "The order list must contain every pop-up exactly once" },
  // drivers.yaml v1.5.0 x-error-codes (M7-150 — verbatim registry copy)
  VEHICLE_TYPE_IN_USE: {
    ar: "لا يزال سائقون أو متاجر يستخدمون نوع المركبة هذا — أخفِه بدل حذفه",
    en: "Drivers or stores still use this vehicle type — hide it instead of deleting it",
  },
  VEHICLE_TYPE_CODE_TAKEN: { ar: "رمز نوع المركبة هذا مستخدم بالفعل", en: "This vehicle type code is already in use" },
  VEHICLE_TYPE_ORDER_INCOMPLETE: {
    ar: "يجب أن يتضمن الترتيب كل أنواع المركبات مرة واحدة بالضبط",
    en: "The order must list every vehicle type exactly once",
  },
};

export interface FieldIssue {
  readonly field: string;
  readonly code: string;
  readonly message: string;
}

export function localeOf(request: TransportRequest): MockLocale {
  return request.headers["Accept-Language"] === "en" ? "en" : "ar";
}

export function mockError(
  status: number,
  code: string,
  locale: MockLocale,
  details?: ReadonlyArray<FieldIssue>,
): TransportResponse {
  const message = MESSAGES[code] ?? { ar: code, en: code };
  return {
    status,
    body: {
      error: {
        code,
        message: message[locale],
        ...(details !== undefined && details.length > 0 ? { details } : {}),
        trace_id: `mock-${crypto.randomUUID()}`,
      },
    },
  };
}

export function ok(body: unknown, status = 200): TransportResponse {
  return { status, body };
}

/** Mutations require an Idempotency-Key (contract-wide rule). */
export function missingIdempotencyKey(request: TransportRequest): boolean {
  return request.method !== "GET" && request.headers["Idempotency-Key"] === undefined;
}

/** Domain mocks accept any bearer issued by the identity mock. */
export function isAuthenticated(request: TransportRequest): boolean {
  return request.headers.Authorization?.startsWith("Bearer ") === true;
}

export interface PageSlice<T> {
  readonly data: ReadonlyArray<T>;
  readonly meta: PageMeta;
}

export function paginate<T>(rows: ReadonlyArray<T>, searchParams: URLSearchParams): PageSlice<T> {
  const page = Math.max(1, Number(searchParams.get("page") ?? "1") || 1);
  const perPage = Math.min(100, Math.max(1, Number(searchParams.get("per_page") ?? "25") || 25));
  const start = (page - 1) * perPage;
  return {
    data: rows.slice(start, start + perPage),
    meta: { page, per_page: perPage, total: rows.length },
  };
}

/** Normalized prefix match on either localized name (geo.yaml search rule). */
export function prefixMatch(search: string, ...names: ReadonlyArray<string>): boolean {
  const needle = search.trim().toLowerCase();
  if (needle === "") return true;
  return names.some((name) =>
    name
      .toLowerCase()
      .split(/\s+/)
      .some((word) => word.startsWith(needle)) || name.toLowerCase().startsWith(needle),
  );
}

export function parsePath(path: string): { readonly segments: ReadonlyArray<string>; readonly searchParams: URLSearchParams } {
  const [pathname = "", query = ""] = path.split("?");
  return {
    segments: pathname.split("/").filter((part) => part !== ""),
    searchParams: new URLSearchParams(query),
  };
}
